import React, { useState } from 'react';
import { Mail, Gift, Coffee, Bell, ArrowRight, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';

const benefits = [
  {
    icon: Gift,
    title: 'Offres Exclusives',
    description: 'Recevez des réductions et promotions réservées aux abonnés',
  },
  {
    icon: Coffee,
    title: 'Nouveautés en Avant-Première',
    description: 'Découvrez nos nouvelles créations avant tout le monde',
  },
  {
    icon: Bell,
    title: 'Événements Spéciaux',
    description: 'Soyez invités à nos dégustations et événements privés',
  },
];

export default function NewsletterPage() {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error('Veuillez entrer votre email');
      return;
    }

    setIsLoading(true);

    try {
      const { data, error } = await supabase.functions.invoke('confirm-newsletter-subscription', {
        body: {
          email: email.trim().toLowerCase(),
          first_name: firstName.trim() || undefined,
        },
      });

      if (error) throw error;

      if (data?.already) {
        toast.info('Vous êtes déjà inscrit à notre newsletter');
        return;
      }

      setIsSubscribed(true);
      toast.success('Bienvenue dans notre communauté !', {
        description: 'Vous recevrez bientôt un email de confirmation.',
      });
    } catch (error) {
      console.error('Newsletter error:', error);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  const pageTitle = "Newsletter | Léger's Coffee Genève";
  const pageDescription =
    "Inscrivez-vous à la newsletter de Léger's Coffee à Genève pour recevoir nos actualités, offres exclusives, nouveautés et invitations à nos événements.";
  const canonicalUrl = 'https://legers-coffee.com/newsletter';
  const ogImage = 'https://legers-coffee.com/og-image.jpg';

  const structuredDataSubscribePage = {
    '@context': 'https://schema.org',
    '@type': 'SubscribeAction',
    name: "Inscription newsletter Léger's Coffee",
    target: canonicalUrl,
    description: pageDescription,
  };

  const structuredDataWebPage = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: "Newsletter | Léger's Coffee",
    url: canonicalUrl,
    description: pageDescription,
    inLanguage: 'fr-CH',
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Léger's Coffee" />
        <meta property="og:locale" content="fr_CH" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">
          {JSON.stringify(structuredDataSubscribePage)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(structuredDataWebPage)}
        </script>
      </Helmet>

      <main data-testid="newsletter-page" className="min-h-screen pt-20">
        {/* Hero */}
        <section className="bg-espresso text-warm-alabaster py-20 lg:py-28">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <div className="w-20 h-20 mx-auto mb-8 rounded-full bg-latte-gold/20 flex items-center justify-center">
              <Mail className="w-10 h-10 text-latte-gold" />
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl font-semibold mb-6">
              Rejoignez Notre{' '}
              <span className="text-latte-gold">Communauté</span>
            </h1>
            <p className="text-warm-alabaster/80 text-lg max-w-2xl mx-auto">
              Inscrivez-vous à notre newsletter et recevez nos actualités,
              offres exclusives et invitations à nos événements.
            </p>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-16 lg:py-20 bg-warm-alabaster">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {benefits.map((benefit, index) => (
                <div
                  key={index}
                  className="bg-white p-8 border border-border-subtle text-center hover:border-latte-gold transition-colors duration-300"
                >
                  <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-warm-secondary flex items-center justify-center">
                    <benefit.icon className="w-6 h-6 text-latte-gold" />
                  </div>
                  <h3 className="font-serif text-lg font-semibold text-espresso mb-3">
                    {benefit.title}
                  </h3>
                  <p className="text-soft-charcoal-secondary text-sm">
                    {benefit.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Subscription Form */}
        <section className="py-16 lg:py-20 bg-warm-secondary">
          <div className="max-w-xl mx-auto px-6 lg:px-12">
            {isSubscribed ? (
              <div className="bg-white p-10 border border-border-subtle text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
                  <Check className="w-10 h-10 text-green-600" />
                </div>
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-4">
                  Merci pour votre inscription !
                </h2>
                <p className="text-soft-charcoal-secondary mb-6">
                  Vous recevrez bientôt un email de bienvenue avec une offre
                  exclusive pour votre première commande.
                </p>
                <Button asChild className="btn-primary">
                  <a href="/menu">
                    Découvrir le Menu
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </a>
                </Button>
              </div>
            ) : (
              <div className="bg-white p-8 lg:p-10 border border-border-subtle">
                <h2 className="font-serif text-2xl text-espresso font-semibold mb-2 text-center">
                  Inscrivez-vous maintenant
                </h2>
                <p className="text-soft-charcoal-secondary text-center mb-8">
                  Recevez{' '}
                  <span className="text-latte-gold font-medium">
                    10% de réduction
                  </span>{' '}
                  sur votre première commande
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      type="text"
                      placeholder="Prénom (optionnel)"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className="input-field h-14"
                      data-testid="newsletter-firstname"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Votre adresse email *"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="input-field h-14"
                      data-testid="newsletter-email"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full btn-primary h-14"
                    data-testid="newsletter-submit"
                  >
                    {isLoading ? (
                      'Inscription...'
                    ) : (
                      <>
                        <Mail className="w-5 h-5 mr-2" />
                        S'inscrire à la Newsletter
                      </>
                    )}
                  </Button>
                </form>

                <p className="text-xs text-soft-charcoal-secondary text-center mt-6">
                  En vous inscrivant, vous acceptez de recevoir nos
                  communications par email. Vous pouvez vous désabonner à tout
                  moment.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Social Proof */}
        <section className="py-16 bg-warm-alabaster">
          <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
            <p className="text-soft-charcoal-secondary">
              Rejoignez plus de{' '}
              <span className="font-semibold text-espresso">
                2,500 amateurs de café
              </span>{' '}
              qui reçoivent déjà nos actualités et offres exclusives.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}